'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
} from '@lastbrand/web-ui/carousel'
import { css } from '@lastbrand/web-ui-styled-system/css'
import classnames from 'classnames'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { forwardRef, useCallback, useEffect, useMemo } from 'react'

import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { CarouselFocusClipRegion, carouselFocusClipViewportCss } from '@src/ds/blocks/carousel-focus-clip'
import { positionBasedCanScroll } from '@src/ds/blocks/carousel-plugins'
import { ProductCard } from '@src/ds/blocks/product-card'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { RichText } from '@src/quince-components/src/molecules'
// Single source of truth for the legacy + DS shared prop shape lives in the molecule directory.
import type { ProductListSectionThreeProps } from '@src/quince-components/src/molecules/productListSectionThree/product-list-section-three.types'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent, trackClickEvent } from '@src/telemetry/track'

const PRODUCT_LIST_SECTION_THREE_DISPLAY_NAME = 'Product List Section Three'

// Header row → product row spacing (Figma `widget/space/48` at lg).

// The `[data-slot="carousel-content"]` and `[data-slot="carousel-item"]` selectors depend on
// `@lastbrand/web-ui/carousel`'s internal slot-name contract. If web-ui renames either slot the
// rules silently stop applying and items fall back to the upstream default widths — the carousel
// tests mock the components and won't catch it. Re-check on every web-ui version bump.
const carouselColumnClass = css({
  display: 'flex',
  flexDirection: 'column',
  width: 'full',
  gap: { base: 'space-24', lg: 'space-48' },
  containerType: 'inline-size',
  // `gap` shorthand sets both row and column. Carousel content is a single row, so the row axis
  // doesn't matter — but emitting both `gap` and `columnGap` shadowed the column axis of `gap`
  // and shipped one wasted declaration. Keep `gap` only.
  '& [data-slot="carousel-content"]': {
    gap: { base: 'space-16', lg: 'space-24' },
    transitionTimingFunction: 'ease-out',
    marginInline: { base: 'auto', lg: 'initial' },
  },
  '& [data-slot="carousel-item"]': {
    minWidth: 0,
    flexGrow: 0,
    flexShrink: 0,
    // STRFRNT-16070: base is a fixed px width, not a viewport percentage. `useDeviceInfo` only
    // distinguishes desktop from non-desktop, so tablet viewports fall into `base`; a `vw` unit
    // there let the QSAL card (and its image) scale up with the wide tablet viewport, rendering
    // far larger than the control/design. A fixed px keeps the card the same physical size on
    // phone and tablet. `lg` keeps the desktop 4-up container-query formula.
    flexBasis: {
      base: '218px',
      lg: 'calc((100cqi - 3 * var(--web-ui-spacing-space-24)) / 4)',
    },
  },
})

const headerRowClass = css({
  display: 'flex',
  alignItems: 'center',
  // eslint-disable-next-line i18next/no-literal-string -- Panda spacing tokens (not user-facing copy)
  gap: 'space-16',
  width: 'full',
})

const headerClass = css({
  // eslint-disable-next-line i18next/no-literal-string -- Panda text style + layout tokens (not user-facing copy)
  textStyle: { base: 'title-24-dec', lg: 'title-32-dec' },
  textAlign: 'left',
  flex: '1 0 0',
  minWidth: 0,
})

const navGroupClass = css({
  // eslint-disable-next-line i18next/no-literal-string -- Panda display value, not user-facing copy
  display: { base: 'none', lg: 'flex' },
  // eslint-disable-next-line i18next/no-literal-string -- Panda spacing tokens (not user-facing copy)
  gap: 'space-8',
  alignItems: 'center',
  flexShrink: 0,
  marginLeft: 'auto',
})

export const ProductListSectionThreeDS = forwardRef<HTMLDivElement, ProductListSectionThreeProps>(
  function ProductListSectionThreeDSEl(props, ref) {
    const {
      preHeadingRichTextProps,
      productCards,
      hideProductOptions,
      hideProductReviews,
      title,
      tracker,
      productClickTrackConfig,
      numberOfSlidesToScroll,
    } = props

    const router = useRouter()
    const deviceInfo = useDeviceInfo()
    const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP

    const productViewTracker = tracker && `${tracker}__productListSectionThree`

    const slidesToScroll = numberOfSlidesToScroll ?? (isDesktop ? 4 : 1)
    const carouselOpts = useMemo(
      () => ({
        align: 'start' as const,
        slidesToScroll,
        containScroll: 'trimSnaps' as const,
        dragFree: true,
      }),
      [slidesToScroll],
    )

    const carouselPlugins = useMemo(() => [WheelGesturesPlugin(), positionBasedCanScroll()], [])

    // Legacy ProductListSectionThree fed its tracker into the Swiper atom, which
    // emitted a BUTTON_CLICK on every prev/next arrow click. The web-ui Carousel
    // buttons ship no telemetry, so mirror that event here to keep QSAL arrow
    // usage flowing into the existing navigation dashboards once the DS gate is
    // on. `value` is the same tracker string legacy emitted, so dashboard filters
    // still match. web-ui invokes this onClick before scrolling (and only skips
    // the scroll if the handler calls preventDefault), so paging still works.
    const trackArrowClick = useCallback(
      (direction: 'left' | 'right') => {
        if (!tracker) return
        trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
          trackGA: true,
          trackInternal: true,
          category: 'navigation',
          action: `click__carousel__${direction}`,
          value: tracker,
        })
      },
      [tracker],
    )

    useEffect(() => {
      if (!productViewTracker) return
      batchedAnalytics.initializeBatchedAnalyticsEvent({
        tracker: productViewTracker,
        eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
        debouncePeriod: 1000,
      })
      batchedAnalytics.trackBatchedAnalytics({
        id: `internalAnalytics-${productViewTracker}`,
        tracker: productViewTracker,
        callback: ({ events }) => {
          const productList = events.map(event => getProductDataForBatchedAnalytics(event))
          const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
          trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
            productList,
            tagsCount,
            ...productClickTrackConfig?.context,
          })
        },
      })
    }, [productViewTracker, productClickTrackConfig?.context])

    const showHeaderRow = Boolean(preHeadingRichTextProps ?? isDesktop)

    return (
      <div
        id={title?.toLowerCase()}
        ref={ref}
        data-testid="product-list-section-three"
        className={classnames(
          css({
            display: 'flex',
            flexDirection: 'column',
            pt: { base: 'space-48', lg: 'space-100' },
            pb: { base: 'space-80', lg: 'space-100' },
          }),
          props.wrapperClassName,
        )}
      >
        <Carousel opts={carouselOpts} plugins={carouselPlugins}>
          <div className={carouselColumnClass}>
            {showHeaderRow ? (
              <div className={headerRowClass}>
                {preHeadingRichTextProps ? (
                  <RichText
                    {...preHeadingRichTextProps}
                    className={classnames(headerClass, preHeadingRichTextProps.className)}
                  />
                ) : null}
                {isDesktop ? (
                  <div className={navGroupClass}>
                    <CarouselPreviousButton
                      onClick={() => {
                        trackArrowClick('left')
                      }}
                    />
                    <CarouselNextButton
                      onClick={() => {
                        trackArrowClick('right')
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Padding/negative-margin gives card focus rings (flush title/swatches) 8px of
                paint room past the viewport's clip; the clip region hands it out per edge. */}
            <CarouselFocusClipRegion>
              <CarouselContent viewportCss={carouselFocusClipViewportCss}>
                {productCards.map((productCard, idx) => {
                  const initialSelectedVariant = getInitialSelectedVariant({
                    cardVariants: productCard.product.cardVariants,
                  })
                  return (
                    <CarouselItem
                      key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                    >
                      <ProductCard
                        productCard={productCard}
                        shouldShowMore
                        hideReviews={hideProductReviews}
                        hideOptions={hideProductOptions}
                        tracker={tracker ? `${tracker}__0__${idx}` : undefined}
                        productViewTracker={productViewTracker}
                        optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                        productClickTrackConfig={
                          productClickTrackConfig
                            ? {
                                ...productClickTrackConfig,
                                context: {
                                  ...productClickTrackConfig.context,
                                  row: 0,
                                  column: idx,
                                  product_id: productCard.product.relatedIds.spid,
                                  product_color: initialSelectedVariant?.displayConfig.value,
                                  contentContainerType:
                                    productClickTrackConfig.context?.contentContainerType ||
                                    CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DISPLAY,
                                  contentContainerName:
                                    productClickTrackConfig.context?.contentContainerName ||
                                    CUSTOM_SECTION_CONTAINER_NAMES.INFINITE_LIST,
                                  contentContainerDisplayName:
                                    productClickTrackConfig.context?.contentContainerDisplayName ||
                                    PRODUCT_LIST_SECTION_THREE_DISPLAY_NAME,
                                },
                              }
                            : undefined
                        }
                        onShowMoreClick={({ selectedVariant }) => {
                          navigateToPDP({
                            slug: productCard.product.slug,
                            tracker,
                            selectedOption: selectedVariant?.displayConfig.value,
                            router,
                          })
                        }}
                      />
                    </CarouselItem>
                  )
                })}
              </CarouselContent>
            </CarouselFocusClipRegion>
          </div>
        </Carousel>
      </div>
    )
  },
)

ProductListSectionThreeDS.displayName = 'ProductListSectionThreeDS'

export { ProductListSectionThreeDS as default }
